import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import logoImg from "../assets/logo_transparent.00f0da9599043373c489.png";

const PURPLE      = [76, 29, 149];
const PURPLE_MID  = [109, 40, 217];
const PALE_BG     = [245, 243, 255];
const DARK        = [20, 20, 20];
const GRAY        = [100, 100, 100];
const WHITE       = [255, 255, 255];
const GREEN       = [21, 128, 61];
const GREEN_LIGHT = [220, 252, 231];
const YELLOW_BG   = [254, 252, 232];
const AMBER       = [202, 138, 4];
const BORDER_CLR  = [216, 180, 254];

// jsPDF Helvetica can't render Rs symbol (shows as apostrophe).
// Use "Rs." prefix instead.
const rupee = (amt) => `Rs. ${amt}`;

export const generateReceipt = async (record, user, userData) => {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth();   // 210
  const H = doc.internal.pageSize.getHeight();  // 297

  const fill  = (c) => doc.setFillColor(c[0], c[1], c[2]);
  const col   = (c) => doc.setTextColor(c[0], c[1], c[2]);
  const drw   = (c) => doc.setDrawColor(c[0], c[1], c[2]);
  const B     = (s) => { doc.setFont("helvetica", "bold");   doc.setFontSize(s); };
  const N     = (s) => { doc.setFont("helvetica", "normal"); doc.setFontSize(s); };

  const fmt = (ts) => {
    if (!ts) return "-";
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
  };

  const receiptNo = `QBR/${Date.now().toString().slice(-6)}`;
  const paidDate  = fmt(record.paidAt || record.createdAt);
  const fullName  = userData
    ? `${userData.firstName || ""} ${userData.lastName || ""}`.trim()
    : (user?.email || "N/A");

  // ════════════════════════════════════════════
  // WHITE BACKGROUND
  // ════════════════════════════════════════════
  fill(WHITE); doc.rect(0, 0, W, H, "F");

  // ════════════════════════════════════════════
  // TOP HEADER — two purple polygons top-right
  // ════════════════════════════════════════════
  const HDR = 52; // header height

  // Lighter polygon (behind)
  fill([196, 181, 253]);
  doc.triangle(105, 0, 140, 0, 110, HDR, "F");
  doc.triangle(105, 0, 110, HDR, 78, HDR, "F");

  // Dark purple main polygon
  fill(PURPLE);
  doc.triangle(135, 0, W, 0, W, HDR, "F");
  doc.triangle(135, 0, W, HDR, 105, HDR, "F");

  // ── Logo (left aligned) ──
  try {
    const res  = await fetch(logoImg);
    const blob = await res.blob();
    const b64  = await new Promise((ok) => {
      const r = new FileReader(); r.onloadend = () => ok(r.result); r.readAsDataURL(blob);
    });
    doc.addImage(b64, "PNG", 12, 8, 20, 20);
  } catch (_) {
    fill(PURPLE); doc.circle(22, 18, 9, "F");
    B(12); col(WHITE); doc.text("Q", 22, 22, { align: "center" });
  }

  // Company name (to the right of logo)
  B(14); col([30, 58, 138]);
  doc.text("Qubright", 36, 16);
  doc.text("Technologies", 36, 23);
  N(8.5); col(GRAY);
  doc.text("Empowering Careers Through Technology", 36, 29);

  // "RECEIPT" text inside dark purple polygon
  // The polygon starts around x=105, and W=210. Centre of polygon ~ x=158
  B(22); col(WHITE);
  doc.text("RECEIPT", 195, 26, { align: "right" });
  N(8); col([196, 181, 253]);
  const tagline = "THANK YOU FOR YOUR PAYMENT";
  doc.text(tagline, 195, 34, { align: "right", charSpace: 0.5 });

  // ════════════════════════════════════════════
  // INFO STRIP
  // ════════════════════════════════════════════
  const STRIP_Y = HDR;
  const STRIP_H = 24;
  fill(PALE_BG); doc.rect(0, STRIP_Y, W, STRIP_H, "F");

  // Receipt icon (simple box with lines)
  drw(PURPLE); doc.setLineWidth(0.5);
  doc.roundedRect(14, STRIP_Y + 5, 10, 13, 1, 1, "D");
  doc.line(17, STRIP_Y + 9, 21, STRIP_Y + 9);
  doc.line(17, STRIP_Y + 12, 21, STRIP_Y + 12);

  N(8); col(GRAY); doc.text("Receipt No.", 28, STRIP_Y + 10);
  B(11); col(DARK); doc.text(receiptNo, 28, STRIP_Y + 17);

  // Calendar icon
  drw(PURPLE); doc.setLineWidth(0.5);
  doc.roundedRect(88, STRIP_Y + 5, 11, 11, 1, 1, "D");
  doc.line(88, STRIP_Y + 9, 99, STRIP_Y + 9);
  doc.line(91, STRIP_Y + 4, 91, STRIP_Y + 6.5);
  doc.line(96, STRIP_Y + 4, 96, STRIP_Y + 6.5);

  N(8); col(GRAY); doc.text("Date of Payment", 103, STRIP_Y + 10);
  B(11); col(DARK); doc.text(paidDate, 103, STRIP_Y + 17);

  // PAID badge
  fill(GREEN_LIGHT); drw(GREEN); doc.setLineWidth(0.5);
  doc.roundedRect(W - 44, STRIP_Y + 7, 30, 10, 5, 5, "FD");
  // Tick circle
  fill(GREEN); doc.circle(W - 39, STRIP_Y + 12, 3, "F");
  drw(WHITE); doc.setLineWidth(0.8);
  doc.line(W - 40.2, STRIP_Y + 12, W - 39.3, STRIP_Y + 13.2);
  doc.line(W - 39.3, STRIP_Y + 13.2, W - 37.8, STRIP_Y + 10.8);
  B(10); col(GREEN); doc.text("PAID", W - 33.5, STRIP_Y + 15);

  // ════════════════════════════════════════════
  // BILLED TO  &  PAYMENT INFORMATION BOXES
  // ════════════════════════════════════════════
  const BOX_Y  = STRIP_Y + STRIP_H + 8;   // ~84
  const BOX_H  = 42;
  const GAP    = 6;
  const BOX_W  = (W - 28 - GAP) / 2;      // ~88mm each

  // ── LEFT BOX: Billed To ──
  fill(WHITE); drw(BORDER_CLR); doc.setLineWidth(0.4);
  doc.roundedRect(14, BOX_Y, BOX_W, BOX_H, 4, 4, "FD");

  // User icon circle
  fill(PURPLE); doc.circle(32, BOX_Y + 21, 10, "F");
  fill(WHITE);  doc.circle(32, BOX_Y + 17.5, 3.5, "F");
  doc.ellipse(32, BOX_Y + 26, 6, 4, "F");

  const LX = 47; // text start x for left box
  B(8); col(PURPLE); doc.text("BILLED TO", LX, BOX_Y + 13);
  B(12); col(DARK);  doc.text(fullName || "Student", LX, BOX_Y + 21);

  // Email row
  drw(PURPLE); doc.setLineWidth(0.4);
  doc.rect(LX, BOX_Y + 24.5, 5, 3.5, "D");
  doc.line(LX, BOX_Y + 24.5, LX + 2.5, BOX_Y + 26);
  doc.line(LX + 2.5, BOX_Y + 26, LX + 5, BOX_Y + 24.5);
  N(9); col(DARK); doc.text(user?.email || "-", LX + 7, BOX_Y + 28);

  // Phone row
  if (userData?.phone) {
    // phone icon lines
    drw(PURPLE); doc.setLineWidth(0.5);
    doc.line(LX + 1, BOX_Y + 32, LX + 2.5, BOX_Y + 35);
    doc.line(LX + 2.5, BOX_Y + 35, LX + 4, BOX_Y + 33);
    N(9); col(DARK); doc.text(userData.phone, LX + 7, BOX_Y + 35);
  }

  // ── RIGHT BOX: Payment Info ──
  const RX_BOX = 14 + BOX_W + GAP;
  fill(WHITE); drw(BORDER_CLR); doc.setLineWidth(0.4);
  doc.roundedRect(RX_BOX, BOX_Y, BOX_W, BOX_H, 4, 4, "FD");

  // Card icon circle
  fill(PURPLE_MID); doc.circle(RX_BOX + 14, BOX_Y + 21, 10, "F");
  drw(WHITE); doc.setLineWidth(0.8);
  doc.roundedRect(RX_BOX + 9, BOX_Y + 18.5, 10, 6, 1, 1, "D");
  doc.line(RX_BOX + 9, BOX_Y + 20.5, RX_BOX + 19, BOX_Y + 20.5);

  const PI_X = RX_BOX + 28; // payment info text x
  B(8); col(PURPLE); doc.text("PAYMENT INFORMATION", PI_X, BOX_Y + 13);

  // Truncate IDs to fit the box (max ~22 chars)
  const truncate = (s, n = 20) => s ? (s.length > n ? s.slice(0, n) + "..." : s) : "-";
  const rows = [
    ["Order ID",    truncate(record.orderId)],
    ["Payment ID",  truncate(record.paymentId)],
    ["Phase",       record.phase     || "-"],
    ["Installment", record.installmentInfo || "-"],
  ];
  rows.forEach(([k, v], i) => {
    const ry = BOX_Y + 20 + i * 6;
    N(8.5); col(GRAY);  doc.text(k, PI_X, ry);
    col(DARK);           doc.text(`:   ${v}`, PI_X + 24, ry);
  });

  // ════════════════════════════════════════════
  // PAYMENT BREAKDOWN
  // ════════════════════════════════════════════
  const TBL_Y = BOX_Y + BOX_H + 10;

  // Icon
  drw(PURPLE); doc.setLineWidth(0.5);
  doc.roundedRect(14, TBL_Y + 2, 8, 10, 1, 1, "D");
  doc.line(16, TBL_Y + 5.5, 20, TBL_Y + 5.5);
  doc.line(16, TBL_Y + 8.5, 20, TBL_Y + 8.5);

  B(13); col(PURPLE); doc.text("Payment Breakdown", 26, TBL_Y + 10);
  drw(PURPLE); doc.line(26, TBL_Y + 12, 78, TBL_Y + 12);

  N(8.5); col(GRAY);
  doc.text("All amounts are in INR (Rs.)", W - 14, TBL_Y + 10, { align: "right" });

  const base  = Number(record.baseAmount || 0).toFixed(2);
  const tax   = Number(record.taxAmount  || 0).toFixed(2);
  const total = Number(record.amount     || 0).toFixed(2);

  autoTable(doc, {
    startY: TBL_Y + 16,
    margin: { left: 14, right: 14 },
    head: [["#", "Description", "Amount (INR)"]],
    body: [
      ["1", `${record.installmentInfo || "Installment"} - ${record.phase || ""}`, rupee(base)],
      ["2", "Platform & Service Charge (2.36%)", rupee(tax)],
    ],
    foot: [["", "TOTAL AMOUNT PAID", rupee(total)]],
    headStyles: {
      fillColor: PURPLE, textColor: WHITE,
      fontStyle: "bold", fontSize: 10,
      cellPadding: { top: 5, bottom: 5, left: 5, right: 5 },
    },
    bodyStyles: {
      fontSize: 10, textColor: DARK,
      cellPadding: { top: 5, bottom: 5, left: 5, right: 5 },
      lineColor: [220, 220, 220], lineWidth: 0.2,
    },
    footStyles: {
      fillColor: PALE_BG, textColor: PURPLE,
      fontStyle: "bold", fontSize: 11,
      cellPadding: { top: 6, bottom: 6, left: 5, right: 5 },
      lineColor: BORDER_CLR, lineWidth: 0.2,
    },
    columnStyles: {
      0: { cellWidth: 14, halign: "center" },
      2: { halign: "right", cellWidth: 48 },
    },
    alternateRowStyles: { fillColor: [250, 248, 255] },
  });

  // ════════════════════════════════════════════
  // IMPORTANT NOTE (diplomatic)
  // ════════════════════════════════════════════
  const NOTE_Y = doc.lastAutoTable.finalY + 10;
  const NOTE_H = 36;

  fill(YELLOW_BG); drw(AMBER); doc.setLineWidth(0.4);
  doc.roundedRect(14, NOTE_Y, W - 28, NOTE_H, 4, 4, "FD");

  // Warning circle
  fill(AMBER); doc.circle(24, NOTE_Y + 8, 4, "F");
  B(10); col(WHITE); doc.text("!", 24, NOTE_Y + 11.5, { align: "center" });

  B(10); col(AMBER); doc.text("IMPORTANT NOTE", 32, NOTE_Y + 10);

  const notes = [
    "This document serves as a payment confirmation for reference purposes only.",
    "It does not constitute a formal tax invoice. A verified copy will be issued upon",
    "confirmation by an authorised Qubright representative.",
    "For payment queries, write to us at: officequbright@gmail.com",
  ];
  N(9); col([113, 63, 18]);
  notes.forEach((ln, i) => {
    doc.text(`\u2022  ${ln}`, 18, NOTE_Y + 18 + i * 5);
  });

  // ════════════════════════════════════════════
  // FOOTER
  // ════════════════════════════════════════════
  const FT_Y = H - 30;
  fill(PURPLE); doc.rect(0, FT_Y, W, 30, "F");

  // Dark triangle accents
  fill([55, 20, 110]);
  doc.triangle(0, H, 25, H, 0, FT_Y + 12, "F");
  doc.triangle(W, FT_Y, W - 35, FT_Y, W, FT_Y + 16, "F");

  // Footer items — evenly spaced: email | website | phone
  const FY1 = FT_Y + 11;
  N(9); col(WHITE);

  // Email icon + text
  drw(WHITE); doc.setLineWidth(0.4);
  doc.rect(18, FY1 - 3.5, 5, 4, "D");
  doc.line(18, FY1 - 3.5, 20.5, FY1 - 1.5);
  doc.line(20.5, FY1 - 1.5, 23, FY1 - 3.5);
  doc.text("officequbright@gmail.com", 26, FY1);

  // Globe icon + text
  doc.circle(80, FY1 - 1.5, 2.5, "D");
  doc.line(77.5, FY1 - 1.5, 82.5, FY1 - 1.5);
  doc.line(80, FY1 - 4, 80, FY1 + 1);
  doc.text("www.qubright.in", 85, FY1);

  // Phone icon + text
  drw(WHITE); doc.setLineWidth(0.5);
  doc.line(139, FY1 - 2.5, 140.5, FY1);
  doc.line(140.5, FY1, 142, FY1 - 1.5);
  doc.text("+91 9353200194  |  +91 7676654809", 144, FY1);

  // Thin divider
  drw([120, 80, 200]); doc.setLineWidth(0.3);
  doc.line(18, FT_Y + 16, W - 18, FT_Y + 16);

  // Bottom centered text
  B(10);  col(WHITE); doc.text("Qubright Technologies", W / 2, FT_Y + 22, { align: "center" });
  N(8.5); col([196, 181, 253]); doc.text("Empowering Careers Through Technology", W / 2, FT_Y + 27, { align: "center" });

  doc.save(`Qubright_Receipt_${receiptNo.replace("/", "_")}.pdf`);
};
